// TopstepX / ProjectX adapter — the first bring-your-own-feed provider
// (D-022). The user's own Topstep API subscription (~$14.50–29/mo, billed
// by Topstep) entitles THEM to live CME data; this adapter displays it to
// them, client-side. Credentials never leave the user's browser.
//
// API (gateway.docs.projectx.com):
//   POST /api/Auth/loginKey        {userName, apiKey} → {token, success}
//   POST /api/Contract/search      {searchText, live} → {contracts:[{id,…}]}
//   POST /api/History/retrieveBars {contractId, live, startTime, endTime,
//                                   unit, unitNumber, limit, includePartialBar}
//     unit: 1=sec 2=min 3=hour 4=day 5=week 6=month
//     contract id format: "CON.F.US.ENQ.U25"

import { storageGet, storageSet, storageRemove } from "../storage.js";
import { MarketDataError, type OHLCPoint } from "../market-data.js";
import { rangeToMs, intervalToMs, type DataProvider, type CandleResult, type ClosesResult } from "./types.js";

const API_BASE = "https://api.topstepx.com";
const CREDS_KEY = "providerTopstepxCreds";
const TOKEN_KEY = "providerTopstepxToken";
const CONTRACTS_KEY = "providerTopstepxContracts";
/** Session tokens are long-lived but undocumented; refresh proactively. */
const TOKEN_TTL_MS = 12 * 3600_000;
const CONTRACT_TTL_MS = 24 * 3600_000; // front month can roll — re-resolve daily

export interface TopstepxCreds {
  userName: string;
  apiKey: string;
}

interface CachedToken {
  token: string;
  at: number;
}

interface CachedContract {
  id: string;
  at: number;
}

/** "GC=F" (our ticker) → "GC" (ProjectX search text). Pure. */
export function projectxSearchText(symbol: string): string {
  return symbol.trim().toUpperCase().replace(/=F$/, "");
}

/** Our interval vocabulary → ProjectX {unit, unitNumber}. Pure. */
export function projectxUnit(interval: string): { unit: number; unitNumber: number } | null {
  const match = /^(\d+)(m|h|d|wk|mo)$/.exec(interval.trim());
  if (!match) return null;
  const n = Number(match[1]);
  switch (match[2]) {
    case "m":
      return n % 60 === 0 ? { unit: 3, unitNumber: n / 60 } : { unit: 2, unitNumber: n };
    case "h":
      return { unit: 3, unitNumber: n };
    case "d":
      return { unit: 4, unitNumber: n };
    case "wk":
      return { unit: 5, unitNumber: n };
    case "mo":
      return { unit: 6, unitNumber: n };
    default:
      return null;
  }
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (response.status === 401) throw new MarketDataError("topstepx-unauthorized");
    if (!response.ok) throw new MarketDataError(`TopstepX ${path} failed: ${response.status}`);
    return (await response.json()) as T;
  } finally {
    clearTimeout(timeout);
  }
}

export async function getTopstepxCreds(): Promise<TopstepxCreds | null> {
  return await storageGet<TopstepxCreds | null>(CREDS_KEY, null);
}

export async function setTopstepxCreds(creds: TopstepxCreds | null): Promise<void> {
  if (creds) await storageSet(CREDS_KEY, creds);
  else await storageRemove([CREDS_KEY, TOKEN_KEY, CONTRACTS_KEY]);
}

async function login(creds: TopstepxCreds): Promise<string> {
  const res = await post<{ token?: string; success: boolean; errorMessage?: string | null }>("/api/Auth/loginKey", {
    userName: creds.userName,
    apiKey: creds.apiKey,
  });
  if (!res.success || !res.token) {
    throw new MarketDataError(res.errorMessage || "TopstepX sign-in failed — check username and API key.");
  }
  await storageSet<CachedToken>(TOKEN_KEY, { token: res.token, at: Date.now() });
  return res.token;
}

async function getToken(forceFresh = false): Promise<string> {
  const creds = await getTopstepxCreds();
  if (!creds) throw new MarketDataError("TopstepX not connected");
  if (!forceFresh) {
    const cached = await storageGet<CachedToken | null>(TOKEN_KEY, null);
    if (cached && Date.now() - cached.at < TOKEN_TTL_MS) return cached.token;
  }
  return await login(creds);
}

/** Runs a call, transparently re-authenticating once on a stale token. */
async function withAuth<T>(run: (token: string) => Promise<T>): Promise<T> {
  try {
    return await run(await getToken());
  } catch (error) {
    if (error instanceof MarketDataError && error.message === "topstepx-unauthorized") {
      return await run(await getToken(true));
    }
    throw error;
  }
}

interface ProjectXContract {
  id: string;
  name?: string;
  description?: string;
  activeContract?: boolean;
}

async function resolveContractId(symbol: string): Promise<string> {
  const cache = await storageGet<Record<string, CachedContract>>(CONTRACTS_KEY, {});
  const cached = cache[symbol];
  if (cached && Date.now() - cached.at < CONTRACT_TTL_MS) return cached.id;

  const searchText = projectxSearchText(symbol);
  const res = await withAuth((token) =>
    post<{ contracts?: ProjectXContract[]; success: boolean }>("/api/Contract/search", { searchText, live: false }, token)
  );
  const contracts = res.contracts ?? [];
  if (!contracts.length) throw new MarketDataError(`TopstepX has no contract matching "${searchText}"`);
  // Prefer the active (front-month) contract; among those, the shortest
  // name is the standard contract rather than a micro/variant.
  const active = contracts.filter((c) => c.activeContract);
  const pool = active.length ? active : contracts;
  const chosen = [...pool].sort((a, b) => (a.name?.length ?? 99) - (b.name?.length ?? 99))[0]!;

  cache[symbol] = { id: chosen.id, at: Date.now() };
  await storageSet(CONTRACTS_KEY, cache);
  return chosen.id;
}

interface ProjectXBar {
  t: string;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

async function topstepxCandles(symbol: string, range: string, interval: string): Promise<CandleResult> {
  const unitSpec = projectxUnit(interval);
  if (!unitSpec) throw new MarketDataError(`TopstepX can't serve interval ${interval}`);
  const contractId = await resolveContractId(symbol);

  const end = new Date();
  const start = new Date(end.getTime() - rangeToMs(range));
  const limit = Math.min(20_000, Math.max(10, Math.ceil(rangeToMs(range) / intervalToMs(interval)) + 2));

  const res = await withAuth((token) =>
    post<{ bars?: ProjectXBar[]; success: boolean; errorMessage?: string | null }>(
      "/api/History/retrieveBars",
      {
        contractId,
        live: false,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        unit: unitSpec.unit,
        unitNumber: unitSpec.unitNumber,
        limit,
        includePartialBar: true,
      },
      token
    )
  );
  if (!res.success) throw new MarketDataError(res.errorMessage || "TopstepX bars request failed");
  const candles: OHLCPoint[] = (res.bars ?? [])
    .map((b) => ({ date: new Date(b.t), open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v ?? 0 }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
  const last = candles[candles.length - 1];
  return { candles, currentPrice: last?.close };
}

/** Round-trips a login so Settings can confirm credentials before saving. */
export async function verifyTopstepxCreds(creds: TopstepxCreds): Promise<{ ok: boolean; message?: string }> {
  try {
    await login(creds);
    return { ok: true };
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : "Could not reach TopstepX." };
  }
}

export const topstepxProvider: DataProvider = {
  id: "topstepx",
  displayName: "TopstepX (your subscription)",
  assetClasses: ["Futures"],
  realtime: true,
  setup: {
    signupUrl: "https://help.topstep.com/en/articles/11187768-topstepx-api-access",
    keyLabel: "TopstepX username + API key",
    costNote: "Uses your own Topstep API subscription (billed by Topstep)",
  },
  byo: {
    fields: [
      { key: "userName", label: "TopstepX username", type: "text" },
      { key: "apiKey", label: "API key", type: "password" },
    ],
    getCreds: async () => (await getTopstepxCreds()) as Record<string, string> | null,
    setCreds: (creds) => setTopstepxCreds(creds ? { userName: creds["userName"] ?? "", apiKey: creds["apiKey"] ?? "" } : null),
    verify: (creds) => verifyTopstepxCreds({ userName: creds["userName"] ?? "", apiKey: creds["apiKey"] ?? "" }),
  },
  candles: topstepxCandles,
  async closes(symbol, range, interval): Promise<ClosesResult> {
    const { candles, currentPrice } = await topstepxCandles(symbol, range, interval);
    return { closes: candles.map((c) => c.close), regularMarketPrice: currentPrice };
  },
};
